import { useMemo, useState } from 'react';
import { motion, fadeInUp, slideInRight, AnimatePresence } from '../lib/motion-utils';
import { useNavigate } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { ArrowLeft, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { calculatePersonalizedRTI } from '../lib/personalization';
import { refreshMicroActions } from '../lib/micro-actions';
import { EmotionalStateGauge } from '../components/EmotionalStateGauge';
import { RTIExplanation } from '../components/RTIExplanations';
import { TriggerSelector } from '../components/TriggerSelector';
import { ContextJournal } from '../components/ContextJournal';
import { InfoHint } from '../components/InfoHint';
import { withErrorBoundary } from '../components/ErrorBoundary';
import { triggerCheckInComplete } from '../hooks/useNotifications';
import { NotificationOnboarding } from '../components/NotificationOnboarding';
import { useSubscriptionPlan } from '../lib/use-subscription-plan';
import { safeLocalStorageGet, safeParseJson, safeReadJsonFromStorage } from '../lib/storage-utils';
import { trackCheckInSaved } from '../lib/analytics';
import { persistServerCheckIn } from '../lib/enterprise-api';
import { CHECKINS_UPDATED_EVENT } from '../lib/cloud-sync';

type StoredCheckIn = {
  timestamp?: string;
  date?: string;
  relationshipId?: string;
  components?: Record<string, number>;
};

function DailyCheckIn() {
  const activeRelationshipId = safeLocalStorageGet('tension_active_relationship');
  const relationshipsRaw = safeReadJsonFromStorage<unknown>('tension_relationships', []);
  const relationships = Array.isArray(relationshipsRaw) ? relationshipsRaw : [];
  const activeRelationship = relationships.find((r: any) => r.id === activeRelationshipId);
  const checkinsRaw = safeReadJsonFromStorage<StoredCheckIn[]>('tension_checkins', []);
  const relationshipCheckins = Array.isArray(checkinsRaw)
    ? checkinsRaw.filter((c) => c?.relationshipId === activeRelationshipId)
    : [];
  const previousCheckIn: StoredCheckIn | null =
    relationshipCheckins.length > 0 ? relationshipCheckins[relationshipCheckins.length - 1]! : null;
  const hadCheckInYesterday = useMemo(() => {
    if (!previousCheckIn?.timestamp && !previousCheckIn?.date) return false;
    const latest = new Date(previousCheckIn.timestamp || previousCheckIn.date);
    if (Number.isNaN(latest.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    latest.setHours(0, 0, 0, 0);
    const daysDiff = Math.floor((today.getTime() - latest.getTime()) / (1000 * 60 * 60 * 24));
    return daysDiff === 1;
  }, [previousCheckIn]);

  const previousComponents = previousCheckIn?.components || {};
  const [step, setStep] = useState<'rti' | 'emotional' | 'triggers' | 'journal'>('rti');
  const [initiation, setInitiation] = useState([typeof previousComponents.initiation === 'number' ? previousComponents.initiation : 50]);
  const [latency, setLatency] = useState([typeof previousComponents.latency === 'number' ? previousComponents.latency : 50]);
  const [emotionalDensity, setEmotionalDensity] = useState([typeof previousComponents.emotionalDensity === 'number' ? previousComponents.emotionalDensity : 50]);
  const [availability, setAvailability] = useState([typeof previousComponents.availability === 'number' ? previousComponents.availability : 50]);
  const [withdrawal, setWithdrawal] = useState([typeof previousComponents.withdrawal === 'number' ? previousComponents.withdrawal : 50]);
  const [emotionalStates, setEmotionalStates] = useState<number[]>([]);
  const [triggers, setTriggers] = useState<string[]>([]);
  const [journalNote, setJournalNote] = useState('');
  const [expressMode, setExpressMode] = useState(hadCheckInYesterday);
  const [loading, setLoading] = useState(false);
  const [showNotificationOnboarding, setShowNotificationOnboarding] = useState(false);
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const { plan: subscription } = useSubscriptionPlan();

  const handleEmotionalStateSelect = (value: number) => {
    if (emotionalStates.length < 5) {
      setEmotionalStates([...emotionalStates, value]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    let savedLocally = false;

    try {
      if (!activeRelationshipId) {
        toast.error('No active relationship found');
        navigate('/relationships');
        return;
      }

      // Validate emotional states selected
      if (!expressMode && emotionalStates.length === 0) {
        toast.error('Please select at least one emotional state');
        setLoading(false);
        return;
      }

      // Calculate emotional average (safe now that we validated length > 0)
      const emotionalAverage = expressMode
        ? Math.round((initiation[0] + latency[0] + emotionalDensity[0] + availability[0] + withdrawal[0]) / 5)
        : Math.round(emotionalStates.reduce((sum, val) => sum + val, 0) / emotionalStates.length);

      const components = {
        initiation: initiation[0],
        latency: latency[0],
        emotionalDensity: emotionalDensity[0],
        availability: availability[0],
        withdrawal: withdrawal[0],
      };

      let rtiScore = calculatePersonalizedRTI(components);
      let rtiMeta: { band?: string; version?: string } = {};

      if (accessToken) {
        try {
          let profileHints: { attachmentStyle?: string; pastPatterns?: string[] } | undefined;
          try {
            const raw = safeLocalStorageGet('tension_user_profile');
            if (raw) {
              const p = safeParseJson<Record<string, unknown>>(raw, {});
              profileHints = {
                attachmentStyle: typeof p?.attachmentStyle === 'string' ? p.attachmentStyle : undefined,
                pastPatterns: Array.isArray(p?.pastPatterns) ? p.pastPatterns as string[] : undefined,
              };
            }
          } catch {
            /* ignore */
          }

          const res = await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/rti/compute`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${publicAnonKey}`,
                'X-User-Token': accessToken,
              },
              body: JSON.stringify({ components, profileHints }),
            },
          );
          if (res.ok) {
            const data = await res.json();
            if (typeof data.score === 'number') rtiScore = data.score;
            if (data.band) rtiMeta.band = data.band;
            if (data.version) rtiMeta.version = data.version;
          }
        } catch {
          /* offline / server — keep client fallback */
        }
      }

      // Store check-in data in local storage
      const now = new Date().toISOString();
      const checkinData = {
        relationshipId: activeRelationshipId,
        relationshipName: activeRelationship?.name || 'Unknown',
        date: now, // Primary date field for data integrity
        timestamp: now, // Backwards compatibility
        components,
        emotionalStates: emotionalStates,
        expressMode,
        emotionalAverage: emotionalAverage,
        triggers: triggers,
        rtiScore: rtiScore,
        rtiBand: rtiMeta.band,
        rtiEngineVersion: rtiMeta.version,
      };

      // Get existing check-ins
      const checkins = safeReadJsonFromStorage<Array<Record<string, unknown>>>('tension_checkins', []);
      
      // Add new check-in
      checkins.push(checkinData);
      
      try {
        localStorage.setItem('tension_checkins', JSON.stringify(checkins));
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(CHECKINS_UPDATED_EVENT));
        }
        savedLocally = true;
      } catch (storageError: any) {
        // Handle QuotaExceededError
        if (storageError.name === 'QuotaExceededError') {
          toast.error('Storage limit reached! Please export your data or delete old check-ins.');
          console.error('❌ localStorage quota exceeded. Check-in count:', checkins.length);
          throw new Error('Storage quota exceeded. Please export your data.');
        }
        throw storageError;
      }
      
      console.log('✅ Check-in saved:', checkinData);

      // Best-effort server persistence for multi-device history and enterprise analytics.
      // A transient backend 404 should not block a successful local check-in.
      const activeOrgId = safeLocalStorageGet('tension_active_org_id');
      try {
        await persistServerCheckIn(
          {
            relationshipId: activeRelationshipId,
            relationshipDomain: 'romantic',
            rtiScore,
            emotionalAverage,
            components,
            triggers,
            metadata: {
              source: 'daily-checkin',
              expressMode,
            },
            organizationId: activeOrgId || null,
          },
          accessToken,
        );
      } catch (persistError) {
        console.warn('Daily check-in server persistence skipped:', persistError);
      }

      trackCheckInSaved({
        source: 'daily',
        totalCheckInsAfterSave: checkins.length,
        rtiScore,
      });

      try {
        await refreshMicroActions(rtiScore, components, { accessToken, source: 'daily' });
      } catch (microActionsError) {
        console.warn('Daily check-in micro-actions refresh skipped:', microActionsError);
      }

      // Calculate streak
      const today = new Date().toDateString();
      const relationshipCheckins = checkins.filter((c: any) => c.relationshipId === activeRelationshipId);
      
      let currentStreak = 1;
      for (let i = relationshipCheckins.length - 2; i >= 0; i--) {
        const rawDate = relationshipCheckins[i]?.date;
        const checkinDate = new Date(typeof rawDate === 'string' ? rawDate : '').toDateString();
        const daysBetween = Math.floor(
          (new Date(today).getTime() - new Date(checkinDate).getTime()) / (1000 * 60 * 60 * 24)
        );
        
        if (daysBetween === currentStreak) {
          currentStreak++;
        } else {
          break;
        }
      }

      // Trigger notification events
      triggerCheckInComplete(currentStreak, rtiScore);

      // Show notification onboarding after first check-in
      if (relationshipCheckins.length === 1) {
        setShowNotificationOnboarding(true);
      } else {
        toast.success(`Check-in complete for ${activeRelationship?.name || 'relationship'}`);
        navigate('/home');
      }
    } catch (error: any) {
      console.error('Check-in error:', error);
      const rawMessage = typeof error?.message === 'string' ? error.message.trim() : '';
      const looksLikeNotFound = /\b404\b/i.test(rawMessage) || /not found/i.test(rawMessage);
      if (savedLocally && looksLikeNotFound) {
        toast.success(`Check-in complete for ${activeRelationship?.name || 'relationship'}`);
        navigate('/home');
        return;
      }
      toast.error(rawMessage || 'Failed to submit check-in');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToEmotional = () => {
    if (expressMode) {
      handleSubmit();
      return;
    }
    setStep('emotional');
  };

  const handleContinueToTriggers = () => {
    setStep('triggers');
  };

  const handleContinueToJournal = () => {
    if (subscription === 'free') {
      // Skip journal for free users
      handleSubmit();
    } else {
      setStep('journal');
    }
  };

  return (
    <div className="min-h-screen tension-page-bg">
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Relationship indicator */}
        {activeRelationship && (
          <div className="mb-6 flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
            <Heart className="w-4 h-4 text-pink-500" aria-hidden="true" />
            <span>{activeRelationship.name}</span>
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 'rti' ? (
            <motion.div
              key="rti"
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <Card className="space-y-8 border-violet-200/70 bg-white/92 p-8 text-neutral-900 shadow-xl shadow-violet-900/10 dark:border-violet-900/45 dark:bg-neutral-900/80 dark:text-neutral-50">
                <div className="space-y-2 text-center">
                  <h2 className="text-2xl font-light text-neutral-900 dark:text-neutral-50 inline-flex items-center gap-1.5">
                    How are things today?
                    <InfoHint
                      title="How this check-in works"
                      description="You move a few sliders about communication, then we turn that into one simple tension score to help you spot patterns."
                    />
                  </h2>
                  <p className="text-neutral-600 dark:text-neutral-300">
                    Drag each slider to match what you’re seeing lately — plain language, your honest read.
                  </p>
                </div>

                {hadCheckInYesterday && (
                  <div className="rounded-lg border border-violet-200 bg-violet-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-violet-900">Adaptive check-in</p>
                        <p className="text-xs text-violet-800">
                          Prefilled from yesterday to save time. Use Express when you want a lighter check-in.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant={expressMode ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setExpressMode((v) => !v)}
                      >
                        {expressMode ? 'Express ON' : 'Express OFF'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Who starts contact */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-base text-neutral-900 dark:text-neutral-100">Who starts contact</Label>
                      <RTIExplanation component="initiation" score={initiation[0]} tier={subscription as any} />
                      <InfoHint
                        title="Who starts contact"
                        description="This shows whether one person is doing most of the reaching out. Big imbalance can create pressure."
                      />
                    </div>
                    <p className="text-sm text-neutral-700 dark:text-neutral-300">
                      Are you usually the one to text or call first, or is it pretty even?
                    </p>
                  </div>
                  <div className="space-y-4 pt-2">
                    <Slider
                      value={initiation}
                      onValueChange={setInitiation}
                      min={0}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-neutral-600 dark:text-neutral-300">
                      <span>Even</span>
                      <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-50">{initiation[0]}%</span>
                      <span>Mostly me</span>
                    </div>
                  </div>
                </div>

                {/* Reply speed */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-base text-neutral-900 dark:text-neutral-100">Reply speed</Label>
                      <RTIExplanation component="latency" score={latency[0]} tier={subscription as any} />
                      <InfoHint
                        title="Reply speed"
                        description="Compares how fast replies are now vs normal. Slower replies can be a distance signal."
                      />
                    </div>
                    <p className="text-sm text-neutral-700 dark:text-neutral-300">
                      Compared to what’s normal for them, are they answering slower lately?
                    </p>
                  </div>
                  <div className="space-y-4 pt-2">
                    <Slider
                      value={latency}
                      onValueChange={setLatency}
                      min={0}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-neutral-600 dark:text-neutral-300">
                      <span>Same as usual</span>
                      <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-50">{latency[0]}%</span>
                      <span>Much slower</span>
                    </div>
                  </div>
                </div>

                {/* Warmth in messages */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-base text-neutral-900 dark:text-neutral-100">Warmth in messages</Label>
                      <RTIExplanation component="emotionalDensity" score={emotionalDensity[0]} tier={subscription as any} />
                      <InfoHint
                        title="Warmth in messages"
                        description="Checks if messages feel caring and emotionally present, or short and flat."
                      />
                    </div>
                    <p className="text-sm text-neutral-700 dark:text-neutral-300">
                      Do their messages feel shorter, colder, or less caring than before?
                    </p>
                  </div>
                  <div className="space-y-4 pt-2">
                    <Slider
                      value={emotionalDensity}
                      onValueChange={setEmotionalDensity}
                      min={0}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-neutral-600 dark:text-neutral-300">
                      <span>Feels normal</span>
                      <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-50">{emotionalDensity[0]}%</span>
                      <span>Very flat</span>
                    </div>
                  </div>
                </div>

                {/* Always available */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-base text-neutral-900 dark:text-neutral-100">Always free for them</Label>
                      <RTIExplanation component="availability" score={availability[0]} tier={subscription as any} />
                      <InfoHint
                        title="Always free for them"
                        description="Measures whether you’re over-available. Too much instant availability can reduce balance."
                      />
                    </div>
                    <p className="text-sm text-neutral-700 dark:text-neutral-300">
                      Do you drop your own plans or answer right away every time they reach out?
                    </p>
                  </div>
                  <div className="space-y-4 pt-2">
                    <Slider
                      value={availability}
                      onValueChange={setAvailability}
                      min={0}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-neutral-600 dark:text-neutral-300">
                      <span>Healthy balance</span>
                      <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-50">{availability[0]}%</span>
                      <span>Always on</span>
                    </div>
                  </div>
                </div>

                {/* Pulling away */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-base text-neutral-900 dark:text-neutral-100">Pulling away</Label>
                      <RTIExplanation component="withdrawal" score={withdrawal[0]} tier={subscription as any} />
                      <InfoHint
                        title="Pulling away"
                        description="Tracks signs of distance like silence, cancelled plans, or avoiding hard conversations."
                      />
                    </div>
                    <p className="text-sm text-neutral-700 dark:text-neutral-300">
                      Any unusual silence, canceled plans, or one of you avoiding the other?
                    </p>
                  </div>
                  <div className="space-y-4 pt-2">
                    <Slider
                      value={withdrawal}
                      onValueChange={setWithdrawal}
                      min={0}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-neutral-600 dark:text-neutral-300">
                      <span>No — feels normal</span>
                      <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-50">{withdrawal[0]}%</span>
                      <span>Yes — avoiding</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleContinueToEmotional}
                  className="w-full"
                  size="lg"
                >
                  {expressMode ? 'Complete Express Check-In' : 'Continue to Emotional Check'}
                </Button>

                <p className="text-center text-xs text-neutral-600 dark:text-neutral-400">
                  Your answers stay private and only help calculate your tension snapshot.
                </p>
              </Card>
            </motion.div>
          ) : step === 'emotional' ? (
            <motion.div
              key="emotional"
              variants={slideInRight}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <Card className="border-violet-200/70 bg-white/92 p-8 dark:border-violet-900/45 dark:bg-neutral-900/80">
                <EmotionalStateGauge
                  selectedStates={emotionalStates}
                  onStateSelect={handleEmotionalStateSelect}
                  maxSelections={5}
                  title={`How do you feel about ${activeRelationship?.name || 'this relationship'}?`}
                  subtitle="Select your emotional state 5 times - be honest with yourself"
                />
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-neutral-600 dark:text-neutral-300">
                  Emotional check
                  <InfoHint
                    title="Emotional check"
                    description="This captures your internal state, not blame. It helps separate anxiety from relationship facts."
                  />
                </div>
                
                <div className="mt-8">
                  <Button
                    onClick={handleContinueToTriggers}
                    disabled={emotionalStates.length < 5 || loading}
                    className="w-full"
                    size="lg"
                  >
                    {loading ? 'Saving...' : 'Continue to Triggers'}
                  </Button>
                </div>
              </Card>
            </motion.div>
          ) : step === 'triggers' ? (
            <motion.div
              key="triggers"
              variants={slideInRight}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <Card className="space-y-6 border-violet-200/70 bg-white/92 p-8 dark:border-violet-900/45 dark:bg-neutral-900/80">
                <div className="space-y-2">
                  <h2 className="text-2xl font-light text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-1.5">
                    Any context tags?
                    <InfoHint
                      title="Context tags"
                      description="Tags like work stress or low energy help explain why a day felt harder, so trends are clearer later."
                    />
                  </h2>
                  <p className="text-neutral-600 dark:text-neutral-400">
                    Tag this check-in with life context. We'll show you patterns later.
                  </p>
                </div>

                <TriggerSelector
                  selectedTriggers={triggers}
                  onTriggersChange={setTriggers}
                  maxTriggers={5}
                />
                
                <div className="space-y-2">
                  <Button
                    onClick={handleContinueToJournal}
                    disabled={loading}
                    className="w-full"
                    size="lg"
                  >
                    Continue
                  </Button>
                  <p className="text-xs text-center text-neutral-500 dark:text-neutral-500">
                    Triggers are optional but help reveal patterns
                  </p>
                </div>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              key="journal"
              variants={slideInRight}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <div className="space-y-4">
                <ContextJournal
                  relationshipId={activeRelationshipId || ''}
                  tier={subscription as any}
                  onSave={(note) => setJournalNote(note)}
                />
                
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full"
                  size="lg"
                >
                  {loading ? 'Saving...' : 'Complete Check-In'}
                </Button>
                
                {subscription !== 'free' && (
                  <Button
                    onClick={handleSubmit}
                    variant="ghost"
                    className="w-full"
                  >
                    Skip Journal
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Notification Onboarding Modal */}
      {showNotificationOnboarding && (
        <NotificationOnboarding
          trigger="first-checkin"
          onComplete={() => {
            setShowNotificationOnboarding(false);
            toast.success('Check-in complete!');
            navigate('/home');
          }}
          onSkip={() => {
            setShowNotificationOnboarding(false);
            toast.success('Check-in complete!');
            navigate('/home');
          }}
        />
      )}
    </div>
  );
}

export default withErrorBoundary(DailyCheckIn);